import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLogin } from '../hooks/useAuth';
import { LoginRequest } from '../models/Auth';
import { ForgotPasswordForm } from './ForgotPasswordForm';
import { GoogleLoginButton } from './GoogleLoginButton';

interface NewLoginFormProps {
  onSuccess?: () => void;
  className?: string;
}

export const NewLoginForm: React.FC<NewLoginFormProps> = ({ onSuccess, className = '' }) => {
  const { t } = useTranslation('auth');
  const loginMutation = useLogin();
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const [formData, setFormData] = useState<LoginRequest>({
    email: '',
    password: '',
  });

  const [fieldErrors, setFieldErrors] = useState<{
    email?: string;
    password?: string;
  }>({});

  const [touched, setTouched] = useState<{
    email?: boolean;
    password?: boolean;
  }>({});

  // 🎯 Validación de campos individuales
  const validateField = (name: keyof LoginRequest, value: string): string | undefined => {
    switch (name) {
      case 'email': {
        if (!value.trim()) return 'El email es requerido';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'El formato del email no es válido';
        return undefined;
      }

      case 'password':
        if (!value.trim()) return 'La contraseña es requerida';
        return undefined;

      default:
        return undefined;
    }
  };

  // 🎯 Manejo de cambios en inputs
  const handleInputChange = (name: keyof LoginRequest, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setTouched((prev) => ({ ...prev, [name]: true }));

    // Limpiar error del campo si existe
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  // 🎯 Validación completa del formulario
  const validateForm = (): boolean => {
    const errors: typeof fieldErrors = {};

    const emailError = validateField('email', formData.email);
    if (emailError) errors.email = emailError;

    const passwordError = validateField('password', formData.password);
    if (passwordError) errors.password = passwordError;

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 🎯 Manejo del submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      await loginMutation.mutateAsync(formData);
      onSuccess?.();
    } catch (error) {
      // Los errores se manejan automáticamente en el hook
      console.error('Error en login:', error);
    }
  };

  // 🎯 Manejo de blur para validación inmediata
  // Solo valida si el usuario llegó a interactuar con el campo: evita que el
  // autoFocus del email dispare "El email es requerido" con el primer click
  // en cualquier otro elemento del formulario (p. ej. el botón de Google).
  const handleBlur = (name: keyof LoginRequest) => {
    if (!touched[name]) return;

    const error = validateField(name, formData[name]);
    if (error) {
      setFieldErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  // Si se muestra forgot password, renderizar ese formulario
  if (showForgotPassword) {
    return <ForgotPasswordForm onBack={() => setShowForgotPassword(false)} className={className} />;
  }

  return (
    <div className={`max-w-96 ${className}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* 🎯 Error general del login */}
        {loginMutation.error && (
          <p className="text-sm text-destructive">{loginMutation.error.message || 'Error al iniciar sesión'}</p>
        )}

        {/* 🎯 Campo Email */}
        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium text-foreground">
            {t('form.email.label', 'Email')}
          </label>
          <Input
            id="email"
            type="email"
            placeholder={t('form.email.placeholder', 'tu@email.com')}
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            onBlur={() => handleBlur('email')}
            className={`${fieldErrors.email ? 'border-destructive' : ''}`}
            autoFocus
            autoComplete="email"
            data-testid="login-email"
          />
          {fieldErrors.email && <p className="text-sm text-destructive">{fieldErrors.email}</p>}
        </div>

        {/* 🎯 Campo Contraseña */}
        <div className="space-y-2">
          <label htmlFor="password" className="text-sm font-medium text-foreground">
            {t('form.password.label', 'Contraseña')}
          </label>
          <Input
            id="password"
            type="password"
            placeholder={t('form.password.placeholder', 'Tu contraseña')}
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            onBlur={() => handleBlur('password')}
            className={`${fieldErrors.password ? 'border-destructive' : ''}`}
            autoComplete="current-password"
            data-testid="login-password"
          />
          {fieldErrors.password && <p className="text-sm text-destructive">{fieldErrors.password}</p>}
        </div>

        {/* 🎯 Botón de envío */}
        <div className="pt-4">
          <Button type="submit" className="w-full" disabled={loginMutation.isPending} data-testid="login-submit">
            {loginMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('login.loading', 'Iniciando sesión...')}
              </>
            ) : (
              t('login.submitButton', 'Iniciar Sesión')
            )}
          </Button>
        </div>

        {/* 🎯 Separador + login con Google */}
        <div className="relative py-2">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              {t('login.orContinueWith', 'O continúa con')}
            </span>
          </div>
        </div>

        <GoogleLoginButton onSuccess={onSuccess} />

        {/* 🎯 Enlace para forgot password */}
        <div className="pt-2 text-center">
          <button
            type="button"
            onClick={() => setShowForgotPassword(true)}
            className="text-sm text-muted-foreground hover:text-foreground underline"
            data-testid="forgot-password-link"
          >
            {t('login.forgotPassword', '¿Olvidaste tu contraseña?')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewLoginForm;
